/**
 * Credits — the unit customers see (.plans/prd-switch-to-credits.md).
 *
 * Every pool and every charge is stored in "cents": `numeric(12,4)` columns
 * that were USD cents before credits existed and are credits now, at the
 * scale 1 credit = 1 stored cent. That scale is the one fact both apps must
 * agree on — the web app renders balances and the runtime's agent tools and
 * emails say the same figures — which is why it lives here and nowhere else.
 *
 * What a credit buys in provider cost is the credit rate, set in the
 * database (`credit_rates`, V438) and applied by the runtime when it charges;
 * nothing here knows or needs it.
 */

/** Stored cents per credit. A constant, not configuration: changing it would
 *  reprice every balance customers hold. */
export const CENTS_PER_CREDIT = 1;

/**
 * The credits a stored amount is worth, as a whole number for display.
 * Floored, never rounded: a figure a customer reads must not promise a credit
 * the pool does not hold (the same rule the old `formatUsd` followed).
 * Negative balances (a pool driven below zero) floor towards zero the same way.
 */
export const wholeCredits = (cents: number): number => Math.floor(cents / CENTS_PER_CREDIT);

/** What a stored amount is worth in whole US dollars at the launch rate,
 *  floored — the "about $X" the referral program quotes beside a grant. */
export const wholeDollars = (cents: number): number => Math.floor(wholeCredits(cents) / 100);

/**
 * The English rendering of a stored amount as credits: whole, grouped,
 * singular at exactly one — "2,500 credits", "1 credit". For surfaces that are
 * English by policy (the admin pages) or by fallback (runtime notices before a
 * language is known). Localized surfaces use their own catalog's plural rule
 * with `wholeCredits`; this exists so the English spelling is not re-derived
 * per surface, where it drifts on the singular.
 */
export const formatCreditsEn = (cents: number): string => {
  const n = wholeCredits(cents);
  return `${n.toLocaleString("en-US")} ${n === 1 ? "credit" : "credits"}`;
};
